/**
 * AuctionCard Temp Version - Shopping Mode
 * PG 심사용으로 경매 관련 워딩을 쇼핑몰 스타일로 변경
 * - 경매사 -> 판매처
 * - 사전입찰 일시 -> 판매 시작일
 * - 경매 기간 -> 판매 종료일
 * - 경매 종료 -> 판매 종료
 */

import React from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import ImageView from '../components/ImageView';
import AuctionHouseComponent from '../components/common/AuctionHouse';

// 경매 카드 컨테이너
const AuctionCardContainer = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease;
  min-height: 370px;
  
  @media (min-width: 768px) {
    min-height: 430px;
  }
  
  @media (min-width: 1024px) {
    min-height: 480px;
  }
  
  &:hover {
    transform: translateY(-2px);
  }
`;

// 이미지 스와이퍼 컨테이너
const ImageSwiperContainer = styled.div`
  width: 100%;
  height: 240px;
  margin-top: 40px;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  position: relative;
  
  @media (min-width: 768px) {
    height: 280px;
  }
  
  @media (min-width: 1024px) {
    height: 320px;
  }
  
  .swiper {
    width: 100% !important;
    height: 100% !important;
    border-radius: 8px;
    overflow: hidden !important;
  }
  
  .swiper-wrapper {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
  }
  
  .swiper-slide {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    flex-shrink: 0 !important;
    min-width: 100% !important;
    max-width: 100% !important;
    
    > div {
      width: 100% !important;
      height: 100% !important;
    }
    
    img {
      width: 100% !important;
      height: 100% !important;
      object-fit: cover !important;
    }
  }
  
  .swiper-pagination {
    bottom: 8px !important;
    z-index: 10;
    
    .swiper-pagination-bullet {
      width: 6px;
      height: 6px;
      background: #ccc !important;
      opacity: 1 !important;
      margin: 0 3px !important;
      border: 1px solid white !important;
    }
    
    .swiper-pagination-bullet-active {
      background: #007bff !important;
      border: 1px solid white !important;
    }
  }
`;

// 옥션 정보 컨테이너
const AuctionInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// 상품명
const AuctionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin: 0;
  line-height: 1.3;
  text-align: left;
  align-self: flex-start;
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// 경매 종료 오버레이
const EndedAuctionOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 8px;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EndedAuctionText = styled.span`
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 0.5px;
`;

// 날짜 정보
const DateInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  width: 100%;
`;

const DateLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #666;
`;

const DateValue = styled.span`
  font-size: 12px;
  color: #666;
`;

// 가격 표시용 컨테이너
const PriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

const PriceUnit = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #666;
`;

const PriceAmount = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #69b5b0;
`;

interface AuctionCardProps {
  auction: any;
  lang: 'ko' | 'en';
  type?: 'ongoing' | 'won' | 'bid';
  onClick?: (auction: any) => void;
  onPayment?: (auction: any) => void;
  user?: any; // 사용자 정보 추가
  extraData?: {
    wonLotCount?: number;
    totalPayment?: number;
    completedAt?: string;
    isPaid?: boolean;
    paymentRequestDate?: string;
    paymentDueDate?: string;
  };
  onFavoriteToggle?: (e: React.MouseEvent, auctionIndex: number) => Promise<void>;
  showFavoriteIcon?: boolean;
  isFavorite?: boolean;
}

const AuctionCardTemp: React.FC<AuctionCardProps> = ({ 
  auction, 
  lang, 
  type = 'ongoing', 
  onClick,
  onPayment,
  user, // user prop 추가
  extraData,
  onFavoriteToggle,
  showFavoriteIcon = true,
  isFavorite = false
}) => {
  
  // 경매가 종료되었는지 확인하는 함수
  const isAuctionEnded = () => {
    if (!auction.auctionEndAt) return false;
    return new Date() > new Date(auction.auctionEndAt);
  };
  
  // 다국어 텍스트 - SHOPPING MODE
  const texts = {
    ko: {
      ongoing: {
        saleStartDate: '판매 시작일', // 사전입찰 일시 -> 판매 시작일
        saleEndDate: '판매 종료일'    // 경매 기간 -> 판매 종료일
      },
      won: {
        purchasedCount: '구매한 상품 개수', // 낙찰된 LOT -> 구매한 상품
        totalPayment: '총 결제 금액',
        purchaseCompleted: '구매 완료', // 입찰 완료 -> 구매 완료
        paymentCompleted: '결제완료',
        paymentPending: '결제대기',
        paymentCompletedDate: '결제완료일',
        paymentRequestDate: '결제요청일',
        paymentDueDate: '결제요청마감일',
        payButton: '결제하기'
      },
      bid: {
        saleStartDate: '판매 시작일',
        saleEndDate: '판매 종료일'
      },
      saleEnded: '판매 종료' // 경매 종료 -> 판매 종료
    },
    en: {
      ongoing: {
        saleStartDate: 'Sale Start Date',
        saleEndDate: 'Sale End Date'
      },
      won: {
        purchasedCount: 'Purchased Items',
        totalPayment: 'Total Payment',
        purchaseCompleted: 'Purchase Completed',
        paymentCompleted: 'Completed',
        paymentPending: 'Pending',
        paymentCompletedDate: 'Payment Completed',
        paymentRequestDate: 'Payment Request Date',
        paymentDueDate: 'Payment Due Date',
        payButton: 'Pay Now'
      },
      bid: {
        saleStartDate: 'Sale Start Date',
        saleEndDate: 'Sale End Date'
      },
      saleEnded: 'Sale Ended'
    }
  };

  // 날짜 포맷팅 함수
  const formatDate = (isoString: string): string => {
    try {
      const targetDate = new Date(isoString);
      const month = targetDate.getMonth() + 1;
      const date = targetDate.getDate();
      const hours = targetDate.getHours().toString().padStart(2, '0');
      const minutes = targetDate.getMinutes().toString().padStart(2, '0');
      
      const dayNamesKo = ['일', '월', '화', '수', '목', '금', '토'];
      const dayNamesEn = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const monthNamesEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const dayName = lang === 'ko' ? dayNamesKo[targetDate.getDay()] : dayNamesEn[targetDate.getDay()];

      if (lang === 'ko') {
        const monthStr = month.toString().padStart(2, '0');
        const dateStr = date.toString().padStart(2, '0');
        return `${monthStr}/${dateStr}(${dayName}) ${hours}:${minutes}`;
      } else {
        const monthName = monthNamesEn[targetDate.getMonth()];
        return `${monthName} ${date}(${dayName}) ${hours}:${minutes} (KR TIME)`;
      }
    } catch (error) {
      return isoString;
    }
  };

  // 이미지 URL 처리 함수
  const getProfileImageSrc = (profileImageUrl?: string | null) => {
    if (!profileImageUrl || profileImageUrl.trim() === "") {
      return "/common/nodata.svg";
    }
    
    if (profileImageUrl.startsWith("http") || profileImageUrl.startsWith("blob:")) {
      return profileImageUrl;
    }
    
    if (profileImageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${profileImageUrl}`;
    }
    
    return `${process.env.REACT_APP_API_HOST}/${profileImageUrl}`;
  };

  // 경매의 모든 이미지 URL을 가져오는 함수
  const getAllImageUrls = (auction: any): string[] => {
    const imageUrls: string[] = [];
    
    if (auction.lots && Array.isArray(auction.lots)) {
      auction.lots.forEach((lot: any) => {
        if (imageUrls.length >= 3) return;
        
        if (lot.images && Array.isArray(lot.images)) {
          lot.images.forEach((imagePath: string) => {
            if (imageUrls.length >= 3) return;
            
            if (imagePath && imagePath.trim() !== "") {
              imageUrls.push(getProfileImageSrc(imagePath));
            }
          });
        }
      });
    }
    
    return imageUrls;
  };

  // 가격을 unit과 amount로 분리하는 함수
  const formatPriceComponents = (price: number): { unit: string; amount: string } => {
    if (lang === 'ko') {
      return {
        unit: 'KRW',
        amount: price.toLocaleString()
      };
    } else {
      const usdPrice = Math.round(price / 1300);
      return {
        unit: 'USD',
        amount: usdPrice.toLocaleString()
      };
    }
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(auction);
    }
  };

  return (
    <AuctionCardContainer onClick={handleCardClick}>
      <ImageSwiperContainer onClick={(e) => e.stopPropagation()}>
        {(() => {
          const imageUrls = getAllImageUrls(auction);
          if (imageUrls.length < 1) {
            return (
              <ImageView
                imageUrl="/common/nodata.svg"
                width={240}
                height={240}
                borderRadius={8}
                alt={auction.name}
                objectFit="cover"
              />
            );
          } else {
            return (
              <Swiper
                modules={[Pagination]}
                slidesPerView={1}
                slidesPerGroup={1}
                spaceBetween={0}
                pagination={{ clickable: true }}
                loop={imageUrls.length > 1}
                centeredSlides={true}
                allowTouchMove={true}
                touchStartPreventDefault={true}
                grabCursor={true}
                style={{ 
                  width: '320px', 
                  height: '290px',
                  maxWidth: '320px',
                  maxHeight: '290px',
                  overflow: 'visible',
                  paddingBottom: '30px'
                }}
              >
                {imageUrls.map((imageUrl, index) => (
                  <SwiperSlide key={index} style={{ 
                    width: '280px', 
                    height: '290px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <ImageView
                      imageUrl={imageUrl}
                      width={280}
                      height={260}
                      borderRadius={8}
                      alt={`${auction.name} ${index + 1}`}
                      objectFit="contain"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            );
          }
        })()}
        
        {/* 판매 종료 오버레이 */}
        {(type === 'ongoing' || type === 'bid') && isAuctionEnded() && (
          <EndedAuctionOverlay>
            <EndedAuctionText>{texts[lang].saleEnded}</EndedAuctionText>
          </EndedAuctionOverlay>
        )}
      </ImageSwiperContainer>
      
      <AuctionInfoContainer>
        <AuctionHouseComponent 
          auctioneerName={auction.auctioneer?.name || '판매처'}
          auctioneerIndex={auction.auctioneer?.index || 0}
        />
        
        <AuctionTitle>{auction.name}</AuctionTitle>
        
      </AuctionInfoContainer>
    </AuctionCardContainer>
  );
};

export default AuctionCardTemp;
